import { FC, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import LoadingPanel from "../../../custom-components/CustomLoader";
import { NullableCollection, ResponseMessageObj, defaultResponseMessageObj } from "../../../types/ApiResults";
import { DEFAULT_PAGE } from "../../../domain/constants";
import { PaginationEnum } from "../../../domain/enums/common/PaginationEnum";
import { ResponseMessage } from "../../../domain/enums/common/ResponseMessageEnum";
import Toast from "../../../custom-components/Toast";
import Pagination from "../../../custom-components/CustomPagination";
import ProductFeaturesTable from "./ProductFeaturesTable";
import ProductFeaturesHeader from "./ProductFeaturesHeader";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import { SelectValue } from "../../../types/SelectProps";
import { useTranslation } from "react-i18next";
import { ProductFeatureDTO } from "../../../domain/models/dto/ProductFeatureDTO";
import { ProductFeaturesCollection } from "../../../domain/models/ProductFeature";
import {
    productFeaturesFiltersFields,
    productFeaturesFiltersSchemaFactory,
} from "../../../validation-schemas/ProductFeaturesFiltersFields";
import { productFeaturesServiceFactory } from "../../../services/ProductFeaturesServiceImpl";

const productFeaturesService = productFeaturesServiceFactory();

const ProductFeaturesPage: FC = () => {
    const { t } = useTranslation();
    const [productFeatures, setProductFeatures] = useState<ProductFeaturesCollection>(NullableCollection);

    const [selectedProductFeature, setSelectedProductFeature] = useState<ProductFeatureDTO>(null);

    const [reloadPage, setReloadPage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
    const [currentLimit, setCurrentLimit] = useState(PaginationEnum.SHOW_20);
    const [messageObj, setMessageObj] = useState<ResponseMessageObj>(defaultResponseMessageObj);

    const getProductFeatures = async (filterValues?: ProductFeatureDTO) => {
        setLoading(true);
        const productFeaturesResult = await productFeaturesService.getAllProductFeaturesService(
            currentPage,
            currentLimit,
            filterValues
        );
        if (productFeaturesResult) {
            setProductFeatures(productFeaturesResult);
            resetSelectedProductFeature();
            setReloadPage(false);
            setMessageObj({
                id: uuidv4(),
                messageType: ResponseMessage.SUCCESS,
                messageKey: "messages.productFeaturesReceived",
            });
        } else {
            setMessageObj({
                id: uuidv4(),
                messageType: ResponseMessage.DANGER,
                messageKey: "errors.common",
            });
        }
        setLoading(false);
    };

    const [searchFiltersFields, setSearchFiltersFields] = useState<ProductFeatureDTO>({} as ProductFeatureDTO);
    const searchFiltersForm = useCustomFormik<ProductFeatureDTO>({
        initial: null,
        fields: productFeaturesFiltersFields,
        schema: productFeaturesFiltersSchemaFactory(t),
        onSubmit: async (values) => {
            setSearchFiltersFields(values);
            setCurrentPage(DEFAULT_PAGE);
            await getProductFeatures(values);
        },
    });

    useEffect(() => {
        getProductFeatures(searchFiltersFields);
    }, [currentLimit, currentPage, reloadPage]);

    const limitHandler = (value: SelectValue) => {
        setCurrentLimit(value as number);
    };

    const pageHandler = (value: number) => {
        setCurrentPage(value);
    };

    const resetSelectedProductFeature = () => {
        setSelectedProductFeature(null);
    };

    const resetMessageObj = () => {
        setMessageObj(defaultResponseMessageObj);
    };

    const resetFiltersHandler = () => {
        searchFiltersForm.form.resetForm();
        setSearchFiltersFields({} as ProductFeatureDTO);
        setReloadPage(true);
    };

    return (
        <div className="relative z-0 bg-light-bg-700 dark:bg-dark-bg-800">
            <LoadingPanel isVisible={loading} />
            <ProductFeaturesHeader
                selectedProductFeature={selectedProductFeature}
                searchFiltersForm={searchFiltersForm.form}
                filtersFields={searchFiltersFields}
                resetSelectedProductFeature={resetSelectedProductFeature}
                limitHandler={limitHandler}
                resetFiltersHandler={resetFiltersHandler}
            />
            <ProductFeaturesTable productFeatures={productFeatures.rows} updateHandler={setSelectedProductFeature} />
            <Pagination currentPage={currentPage} totalPages={productFeatures.totalPages} pageHandler={pageHandler} />
            <Toast messageObj={messageObj} resetMessageObj={resetMessageObj} />
        </div>
    );
};

export default ProductFeaturesPage;
